// EM-H: operator DLQ replay idempotency ledger.
//
// WHY: DLQ-B (#802) + EM-G (#836) shipped forensic R2 archive and auto
// re-enqueue, but never gave operators a way to *consciously* replay a single
// archived message. Without a ledger, an operator who clicks "Replay" twice
// (or two operators clicking simultaneously) would re-deliver the same message
// to the source topic — duplicating side effects (charges, emails, webhooks).
//
// This table is the idempotency receipt: one row per (dlq, message_id, force)
// attempt. INSERT ... ON CONFLICT DO NOTHING gives us atomic "first writer
// wins" semantics without a read-then-write TOCTOU window. The `force` flag
// lets us distinguish a re-run that intentionally bypasses idempotency from a
// duplicate click; we still record both rows so the audit trail is complete.
//
// Tenant scoping: tenant_id is copied from the archived event body so that
// per-tenant audit queries return only that tenant's replays even though the
// replay action itself is a super_admin action against the platform DB.
//
// No PII lives here — only IDs, the dlq name, and timestamps. The full
// archived payload stays in R2 (encrypted at rest).

import {
  pgTable,
  text,
  timestamp,
  uuid,
  boolean,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const dlqReplays = pgTable(
  "dlq_replays",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // R2 archive key components — together they locate the source payload.
    dlq: text("dlq").notNull(),
    messageId: text("message_id").notNull(),
    // tenantId is extracted from the archived event body. It MAY be "platform"
    // for tenant-less platform events, so it is stored as text — not uuid —
    // to avoid an INSERT failure on the very rows we most need an audit row for.
    tenantId: text("tenant_id").notNull(),
    eventType: text("event_type"),
    sourceTopic: text("source_topic").notNull(),
    replayedBy: uuid("replayed_by").notNull(),
    replayedAt: timestamp("replayed_at", tz).notNull().defaultNow(),
    forceUsed: boolean("force_used").notNull().default(false),
  },
  (table) => [
    // Idempotency anchor: same (dlq, message_id) cannot land twice unless
    // a force=true row is explicitly recorded.
    uniqueIndex("uq_dlq_replays_dlq_message_force").on(
      table.dlq,
      table.messageId,
      table.forceUsed,
    ),
    index("idx_dlq_replays_tenant_time").on(table.tenantId, table.replayedAt),
    index("idx_dlq_replays_dlq_time").on(table.dlq, table.replayedAt),
  ],
);

export type DlqReplayRow = typeof dlqReplays.$inferSelect;
export type DlqReplayInsert = typeof dlqReplays.$inferInsert;
